import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import LockIcon from "@mui/icons-material/Lock"

import { buildSeriePreview } from "./buildSeriePreview"
import { useCreateSerie, useUpdateSerie } from "./hooks/useSeries"

const STRUCTURAL_FIELDS = [
  "prefix1",
  "prefix2",
  "suffix1",
  "start",
  "padding",
  "separator",
]

const emptyForm = {
  name: "",
  prefix1: "",
  prefix2: "",
  suffix1: "",
  separator: "-",
  start: "1",
  padding: "5",
}

const toForm = serie => {
  if (!serie) return { ...emptyForm }
  return {
    name: serie.name ?? "",
    prefix1: serie.prefix1 ?? "",
    prefix2: serie.prefix2 ?? "",
    suffix1: serie.suffix1 ?? "",
    separator: serie.separator ?? "-",
    start: String(serie.start ?? 1),
    padding: String(serie.padding ?? 5),
  }
}

const SerieFormModal = ({
  open,
  onClose,
  organizationId,
  serieType,
  serie,
}) => {
  const isEdit = Boolean(serie)
  const isStarted = Boolean(serie?.started)

  const [form, setForm] = useState(toForm(serie))
  const [error, setError] = useState(null)

  const createMutation = useCreateSerie(organizationId, serieType)
  const updateMutation = useUpdateSerie(organizationId, serieType)
  const saving = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (open) {
      setForm(toForm(serie))
      setError(null)
    }
  }, [open, serie])

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const previewArgs = useMemo(
    () => ({
      prefix1: form.prefix1,
      prefix2: form.prefix2,
      suffix1: form.suffix1,
      padding: parseInt(form.padding, 10) || 0,
      separator: form.separator,
      start: parseInt(form.start, 10) || 1,
    }),
    [
      form.prefix1,
      form.prefix2,
      form.suffix1,
      form.padding,
      form.separator,
      form.start,
    ],
  )

  const preview = buildSeriePreview(previewArgs)

  const isStructuralDisabled = field =>
    isEdit && isStarted && STRUCTURAL_FIELDS.includes(field)

  const lockTip = "No se puede modificar: la serie ya emitió documentos."

  const onSave = async () => {
    setError(null)

    const start = Math.max(1, parseInt(form.start, 10) || 1)
    const padding = Math.max(0, parseInt(form.padding, 10) || 0)
    const trimmedName = (form.name ?? "").trim()
    const name = trimmedName === "" ? null : trimmedName

    try {
      if (isEdit) {
        // PATCH: solo enviar lo que cambió y no esté lockeado
        const body = { name }
        if (!isStarted) {
          body.prefix1 = form.prefix1.trim()
          body.prefix2 = form.prefix2.trim()
          body.suffix1 = form.suffix1.trim()
          body.separator = form.separator
          body.start = start
          body.padding = padding
        }
        await updateMutation.mutateAsync({ serieSK: serie.SK, body })
      } else {
        const body = {
          name,
          prefix1: form.prefix1.trim(),
          prefix2: form.prefix2.trim(),
          suffix1: form.suffix1.trim(),
          separator: form.separator,
          start,
          padding,
        }
        await createMutation.mutateAsync(body)
      }
      onClose()
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Error al guardar la serie"
      setError(detail)
    }
  }

  const renderField = (field, label, type = "text", extraProps = {}) => {
    const disabled = isStructuralDisabled(field)
    const tf = (
      <TextField
        label={label}
        value={form[field]}
        onChange={e => set(field, e.target.value)}
        onBlur={() => set(field, (form[field] ?? "").toString().trim())}
        disabled={disabled}
        type={type}
        fullWidth
        size="small"
        margin="normal"
        InputProps={
          disabled
            ? {
                endAdornment: <LockIcon fontSize="small" color="disabled" />,
              }
            : undefined
        }
        {...extraProps}
      />
    )
    return disabled ? <Tooltip title={lockTip}>{tf}</Tooltip> : tf
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Editar serie" : "Nueva serie"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isStarted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta serie ya emitió documentos. Solo se puede editar el nombre.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Próximos números (preview)"
          value={preview}
          disabled
          fullWidth
          size="small"
          margin="normal"
          sx={{ mb: 2 }}
        />

        {renderField("name", "Nombre (opcional)")}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            {renderField("prefix1", "Prefijo 1")}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderField("prefix2", "Prefijo 2")}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderField("suffix1", "Sufijo")}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderField("separator", "Separador")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderField("start", "Primer número", "number", {
              inputProps: { min: 1, step: 1 },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderField("padding", "Padding (ceros)", "number", {
              inputProps: { min: 0, max: 15, step: 1 },
            })}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="branded"
          variant="outlined"
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          color="branded"
          variant="contained"
          disabled={saving}
          sx={{ minWidth: 106 }}
        >
          {saving ? (
            <CircularProgress size={22} sx={{ color: "white" }} />
          ) : (
            "Guardar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SerieFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  serieType: PropTypes.string.isRequired,
  serie: PropTypes.object, // null/undefined when creating
}

export default SerieFormModal
